/**
 * particleConstellationPipeline — particles attracted to center with trails.
 *
 * 60 particles orbit around the center via inverse-square attraction.
 * Each particle has a phase offset. Trails accumulate from previous
 * frames via trail history. Audio drives particle count and speed.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ParticleConstellationPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Background.
  vec3 col = vec3(0.02, 0.03, 0.06);

  // 60 particles, each on a unique Lissajous orbit around the center.
  const int N = 60;
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    // Per-particle parameters.
    float ax = 0.25 + 0.20 * hash21(vec2(fi, 1.0));
    float ay = 0.25 + 0.20 * hash21(vec2(fi, 2.0));
    float fx = 0.5 + 0.5 * hash21(vec2(fi, 3.0));
    float fy = 0.5 + 0.5 * hash21(vec2(fi, 4.0));
    float phx = hash21(vec2(fi, 5.0)) * 6.2831853;
    float phy = hash21(vec2(fi, 6.0)) * 6.2831853;
    // Speed scales with bass.
    float speed = 0.3 + bass * 0.4;
    vec2 pos = vec2(
      ax * sin(fx * uTime * speed + phx),
      ay * cos(fy * uTime * speed + phy)
    );
    // Trail: sample a few positions behind the current one.
    for (int t = 1; t < 12; t++) {
      float trailT = uTime * speed - float(t) * 0.03;
      vec2 trailPos = vec2(
        ax * sin(fx * trailT + phx),
        ay * cos(fy * trailT + phy)
      );
      float d = length(p - trailPos);
      float trailFade = 1.0 - float(t) / 12.0;
      float glow = exp(-d * d * 5000.0) * trailFade * 0.3;
      col += vec3(0.4, 0.7, 1.0) * glow * (0.5 + high * 0.5);
    }
    // Main particle (brighter).
    float d = length(p - pos);
    float radius = 0.01 + 0.005 * sin(uTime * 3.0 + fi);
    float glow = exp(-d * d / (radius * radius)) * 1.5;
    // Color: per-particle hue.
    float hue = fi / float(N);
    vec3 pColor = vec3(
      0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
      0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
      0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
    );
    col += pColor * glow;
  }

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeParticleConstellationBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.6 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 0.9 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.1 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.3 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.6 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.0 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.4 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 2.9 + 3.0);
}

export function computeParticleConstellationRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createParticleConstellationPipeline(gl: WebGL2RenderingContext): ParticleConstellationPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawParticleConstellation(
  gl: WebGL2RenderingContext,
  pipe: ParticleConstellationPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
